import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';
import ScrollController from '../../../assets/js/controllers/scroll';

import Color from '../../../assets/js/constants/color';

import { classesActiveAndIn, classesDelayedOut, getRootOffsetBounds, mq, multiClassList } from '../../../assets/js/helpers';

export default class NavigationAnchor {

	static selector = 'navigation-anchor';

	// class variables

	el = {};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		container: {
			height: null
		},
		footer: null,
		hero: null,
		sectionsAll: [],
		sectionsAnchored: []
	};

	flags = {
		isMobile: false,
		isSticky: false,
		isStacked: false,
		willStack: false,
		willStick: false
	};

	states = {
		color: null,
		activeSection: null,
		activeSectionAnchored: null
	};

	timeouts = {
		unstick: null,
		unstack: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,
			ctas: container.querySelectorAll('ul > li > a'),
			hero: document.body.querySelector('.hero'),
			footer: document.body.querySelector('.footer'),
			sectionsAll: document.body.querySelectorAll('main > div:not(.navigation)'),
			sectionsAnchored: []
		};

		for (let i = 0; i < this.el.ctas.length; i++) {
			const $cta = this.el.ctas[i];
			const $scrollSection = document.body.querySelector(`[id=${$cta.getAttribute('href').substr(1)}]`);
			if ($scrollSection) {
				this.el.sectionsAnchored.push($scrollSection);
			}
		}

		this.initialize();
	}

	initialize() {
		this.#addEventListeners();
	}

	// event listeners

	#addEventListeners = () => {
		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.controllers.animation = AnimationController.set({
			update: this.#onAnimationUpdate,
			render: this.#onAnimationRender
		});

		this.#measureResize();
	}

	#removeEventListeners = () => {
		ResizeController.clear(this.controllers.resize);
		AnimationController.clear(this.controllers.animation);
	}

	// listener methods

	#onResizeUpdate = () => {
		const wasMobile = this.flags.isMobile;
		let willRender = false;

		this.flags.isMobile = !mq('medium');

		if (this.flags.isMobile) {
			if (!wasMobile) {
				willRender = true;
			}
		} else if (wasMobile) {
			willRender = true;
		}

		this.#measureResize();

		return willRender;
	}

	#onResizeRender = () => {
		if (this.flags.isMobile && this.el.container.classList.contains('is-sticky')) {
			this.unstick();
		}
	}

	#onAnimationUpdate = () => this.measure();
	#onAnimationRender = () => this.adjust();

	// measure scroll position and behave accordingly

	measure() {
		const scrollTop = ScrollController.top;
		const scrollCenter = ScrollController.top + window.innerHeight / 2;
		let curColor = this.states.color;
		let willRender = false;

		const willStick = this.measureStickStatus();

		if (this.flags.willStick !== willStick) {
			this.flags.willStick = willStick;
			willRender = true;
		}

		const willStack = this.measureStackStatus();

		if (this.flags.willStack !== willStack) {
			this.flags.willStack = willStack;
			willRender = true;
		}

		// update active link based on scroll position

		let $activeSectionAnchored = this.states.activeSectionAnchored || this.el.sectionsAnchored[0];

		for (let i = 1; i < this.el.sectionsAnchored.length; i++) {
			const $scrollSection = this.el.sectionsAnchored[i];

			if ($scrollSection !== this.states.activeSectionAnchored) {

				const prevSectionCoords = (i > 0) ? this.coords.sectionsAnchored[i - 1] : null;
				const prevSectionBottom = prevSectionCoords ? prevSectionCoords.bottom : 0;

				const nextSectionCoords = (i < this.el.sectionsAnchored.length - 1) ? this.coords.sectionsAnchored[i + 1] : null;
				const nextSectionTop = nextSectionCoords ? nextSectionCoords.top : document.documentElement.scrollHeight;

				if ((scrollCenter > prevSectionBottom && scrollCenter < nextSectionTop)) {
					$activeSectionAnchored = $scrollSection;
				}
			}
		}

		if (this.states.activeSectionAnchored !== $activeSectionAnchored) {
			this.states.activeSectionAnchored = $activeSectionAnchored;
			willRender = true;
		}

		// set background color

		let $activeSection = this.states.activeSection || this.el.sectionsAll[0];

		for (let i = 1; i < this.el.sectionsAll.length; i++) {

			const $scrollSection = this.el.sectionsAll[i];

			if ($scrollSection !== this.states.activeSection) {

				const sectionCoords = this.coords.sectionsAll[i];
				const sectionTop = sectionCoords.top;

				const nextSectionCoords = (i < this.el.sectionsAll.length - 1) ? this.coords.sectionsAll[i + 1] : null;
				const nextSectionTop = nextSectionCoords ? nextSectionCoords.top : document.documentElement.scrollHeight;

				if ((scrollTop >= sectionTop && scrollTop < nextSectionTop)) {
					$activeSection = $scrollSection;
					if ($activeSection.classList.contains('is-dark')) {
						curColor = Color.DARK;
					} else if ($activeSection.classList.contains('is-grey')) {
						curColor = Color.GREY;
					} else {
						curColor = Color.LIGHT;
					}
				}
			}
		}

		if ($activeSection !== this.states.activeSection) {
			this.states.activeSection = $activeSection;
		}

		if (this.states.color !== curColor) {
			this.states.color = curColor;
			willRender = true;
		}

		return willRender;
	}

	adjust() {
		this.adjustStickStatus();
		this.adjustStackStatus();

		if (this.states.color === Color.DARK) {
			this.el.container.classList.add('is-dark');
			this.el.container.classList.remove('is-light', 'is-grey');
		} else if (this.states.color === Color.GREY) {
			this.el.container.classList.add('is-grey');
			this.el.container.classList.remove('is-light', 'is-dark');
		} else {
			this.el.container.classList.add('is-light');
			this.el.container.classList.remove('is-grey', 'is-dark');
		}

		this.renderActiveSection();
	}

	// measurement

	#measureResize = () => {
		this.coords.container.height = this.el.container.offsetHeight;
		this.coords.footer = getRootOffsetBounds(this.el.footer);
		this.coords.hero = getRootOffsetBounds(this.el.hero);
		this.coords.hero.bottom += parseInt(getComputedStyle(this.el.hero).marginBottom);

		// calculate coords of each section

		this.coords.sectionsAnchored = [];

		for (let i = 0; i < this.el.sectionsAnchored.length; i++) {
			const $sectionAnchored = this.el.sectionsAnchored[i];
			const sectionAnchoredCoords = getRootOffsetBounds($sectionAnchored);
			this.coords.sectionsAnchored.push(sectionAnchoredCoords);
		}

		this.coords.sectionsAll = [];

		for (let i = 0; i < this.el.sectionsAll.length; i++) {
			const $section = this.el.sectionsAll[i];
			const sectionCoords = getRootOffsetBounds($section);
			this.coords.sectionsAll.push(sectionCoords);
		}
	}

	// show / hide

	stick() {
		if (!this.flags.isSticky) {
			this.flags.willStick = true;
			this.flags.isSticky = true;
			this.el.container.classList.remove('is-sticky-out');

			if (this.timeouts.unstick) {
				clearTimeout(this.timeouts.unstick);
				this.timeouts.unstick = null;
			}

			ScrollController.stick(this.el.container);
			classesActiveAndIn(this.el.container, 'is-sticky', 'is-sticky-in').then(() => {
				if (!this.flags.isSticky) {
					this.el.container.classList.remove('is-sticky', 'is-sticky-in');
				}
			});
		}
	}

	unstick() {
		if (this.flags.isSticky) {
			this.flags.willStick = false;
			this.flags.isSticky = false;

			this.timeouts.unstick = classesDelayedOut(this.el.container, 400, 'is-sticky', 'is-sticky-in', 'is-sticky-out', () => {
				this.timeouts.unstick = null;
				ScrollController.unstick(this.el.container);
				if (this.measureStickStatus()) {
					this.adjustStickStatus();
				}
			});

			this.unstack();
		}
	}

	stack() {
		return false;
		/*
		if (!this.flags.isStacked) {
			this.flags.willStack = true;
			this.flags.isStacked = true;
			this.el.container.classList.remove('is-stacked-out');

			if (this.timeouts.unstack) {
				clearTimeout(this.timeouts.unstack);
				this.timeouts.unstack = null;
			}

			classesActiveAndIn(this.el.container, 'is-stacked', 'is-stacked-in').then(() => {
				if (!this.flags.isStacked) {
					this.el.container.classList.remove('is-stacked', 'is-stacked-in');
				}
			});
		}
		*/
	}

	unstack() {
		if (this.flags.isStacked) {
			this.flags.willStack = false;
			this.flags.isStacked = false;

			this.timeouts.unstack = classesDelayedOut(this.el.container, 400, 'is-stacked', 'is-stacked-in', 'is-stacked-out', () => {
				this.timeouts.unstack = null;
				if (this.measureStackStatus()) {
					this.adjustStackStatus();
				}
			});
		}
	}

	//

	measureStickStatus() {
		const scrollTop = ScrollController.top;
		const scrollBottom = scrollTop + window.innerHeight;
		let willStick = true;

		// check hide / show status based on hero

		if (scrollTop < this.coords.hero.bottom || scrollBottom > this.coords.footer.top) {
			willStick = false;
		} else if (ScrollController.direction < 0) {
			willStick = false; // remove for stacking
		}

		return willStick;
	}

	adjustStickStatus() {
		if (this.flags.willStick) {
			this.stick();
		} else {
			this.unstick();
		}
	}

	//

	measureStackStatus() {
		let willStack = false;

		// check hide / show status based on hero

		if (this.flags.isSticky && ScrollController.direction < 0) {
			willStack = true;
		}

		return willStack;
	}

	adjustStackStatus() {
		if (this.flags.willStack) {
			this.stack();
		} else {
			this.unstack();
		}
	}

	//

	renderActiveSection() {
		multiClassList(this.el.ctas, 'remove', 'is-active');
		for (let i = 0; i < this.el.sectionsAnchored.length; i++) {
			const $scrollSection = this.el.sectionsAnchored[i];
			if ($scrollSection === this.states.activeSection) {
				this.el.ctas[i].classList.add('is-active');
				break;
			}
		}
	}

	//

	destroy() {
		this.#removeEventListeners();
	}

}
